import { useState } from "react";
import { toast } from "sonner";
import { X, PencilSimple, Trash } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const EMPTY_FORM = { name: "", document: "", contact_name: "", email: "", phone: "", notes: "" };

function SupplierRow({ s, onEdit, onDelete }) {
  return (
    <tr data-testid={`sup-row-${s.id}`}>
      <td className="font-semibold">{s.name}</td>
      <td className="font-mono text-xs">{s.document || "—"}</td>
      <td className="text-xs">{s.contact_name || "—"}</td>
      <td className="font-mono text-xs">{s.phone || "—"}</td>
      <td className="text-right whitespace-nowrap">
        <button className="text-xs underline mr-3" onClick={() => onEdit(s)}><PencilSimple size={12} className="inline mr-1" />Editar</button>
        <button className="text-xs underline text-red-600" onClick={() => onDelete(s.id)} data-testid={`sup-remove-${s.id}`}><Trash size={12} className="inline mr-1" />Remover</button>
      </td>
    </tr>
  );
}

const formFromSupplier = (s) => ({
  name: s.name || "",
  document: s.document || "",
  contact_name: s.contact_name || "",
  email: s.email || "",
  phone: s.phone || "",
  notes: s.notes || "",
});

/**
 * Modal de gerenciamento de fornecedores usado dentro da tela de Contas a Pagar.
 * Extraído de AccountsPayable.jsx para reduzir o tamanho do arquivo pai.
 */
export default function SuppliersModal({ suppliers: initial, onClose }) {
  const [rows, setRows] = useState(initial || []);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    const { data } = await api.get("/suppliers");
    setRows(data);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await api.put(`/suppliers/${editing.id}`, form);
      else await api.post("/suppliers", form);
      setForm(EMPTY_FORM);
      setEditing(null);
      reload();
      toast.success(editing ? "Fornecedor atualizado" : "Fornecedor cadastrado");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!confirm("Remover fornecedor?")) return;
    await api.delete(`/suppliers/${id}`);
    reload();
  };

  const edit = (s) => {
    setEditing(s);
    setForm(formFromSupplier(s));
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6 overflow-y-auto" onClick={onClose}>
      <div className="glass max-w-3xl w-full p-8 my-6" onClick={(e) => e.stopPropagation()} data-testid="suppliers-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline text-zinc-500">FORNECEDORES</div>
            <h2 className="font-head text-2xl font-black">Gerenciar Fornecedores</h2>
          </div>
          <button onClick={onClose} data-testid="close-suppliers"><X size={20} /></button>
        </div>

        <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-6 gap-3 mb-6 items-end border border-black bg-white p-4">
          <div className="md:col-span-2"><label className="label">Nome *</label><input className="input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sup-name" /></div>
          <div><label className="label">CNPJ/CPF</label><input className="input font-mono" value={form.document} onChange={(e) => setForm({ ...form, document: e.target.value })} data-testid="sup-document" /></div>
          <div><label className="label">Contato</label><input className="input" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></div>
          <div><label className="label">Telefone</label><input className="input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          <div className="md:col-span-6 flex gap-2">
            <button type="submit" disabled={saving} className="btn" data-testid="sup-save">{editing ? "Atualizar" : "Cadastrar"}</button>
            {editing && <button type="button" className="btn btn-ghost" onClick={cancelEdit}>Cancelar edição</button>}
          </div>
        </form>

        <div className="border border-black bg-white overflow-x-auto">
          <table className="table">
            <thead><tr><th>Nome</th><th>Documento</th><th>Contato</th><th>Telefone</th><th></th></tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-zinc-400">Nenhum fornecedor.</td></tr>}
              {rows.map((s) => (
                <SupplierRow key={s.id} s={s} onEdit={edit} onDelete={del} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
